import type { RiskFlowAlert } from "./riskflow-feed";

export function riskFlowNotificationMetadata(
  alert: Pick<
    RiskFlowAlert,
    | "subScores"
    | "headlineWeight"
    | "headlineIvScore"
    | "headlineEventWeight"
    | "headlineFreshness"
    | "headlineImpactWeight"
    | "headlineWeightSource"
    | "pointRange"
    | "direction"
    | "instrument"
  >,
): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  if (alert.subScores) metadata.subScores = alert.subScores;
  copyNumber(metadata, "headlineWeight", alert.headlineWeight);
  copyNumber(metadata, "headlineIvScore", alert.headlineIvScore);
  copyNumber(metadata, "headlineEventWeight", alert.headlineEventWeight);
  copyNumber(metadata, "headlineFreshness", alert.headlineFreshness);
  copyNumber(metadata, "headlineImpactWeight", alert.headlineImpactWeight);
  copyNumber(metadata, "pointRange", alert.pointRange);
  if (alert.headlineWeightSource)
    metadata.headlineWeightSource = alert.headlineWeightSource;
  if (alert.direction) metadata.direction = alert.direction;
  if (alert.instrument) metadata.instrument = alert.instrument;
  return metadata;
}

export function riskFlowAlertScoringFromMetadata(
  metadata: Record<string, unknown> | undefined,
): Partial<RiskFlowAlert> {
  if (!metadata) return {};
  return {
    subScores: subScoresFromMetadata(metadata.subScores),
    headlineWeight: numberFromMetadata(metadata.headlineWeight),
    headlineIvScore: numberFromMetadata(metadata.headlineIvScore),
    headlineEventWeight: numberFromMetadata(metadata.headlineEventWeight),
    headlineFreshness: numberFromMetadata(metadata.headlineFreshness),
    headlineImpactWeight: numberFromMetadata(metadata.headlineImpactWeight),
    headlineWeightSource: weightSourceFromMetadata(
      metadata.headlineWeightSource,
    ),
    pointRange: numberFromMetadata(metadata.pointRange),
    direction: directionFromMetadata(metadata.direction),
    instrument: stringFromMetadata(metadata.instrument),
  };
}

function copyNumber(
  target: Record<string, unknown>,
  key: string,
  value: number | null | undefined,
) {
  if (typeof value === "number" && Number.isFinite(value)) target[key] = value;
}

function numberFromMetadata(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringFromMetadata(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function directionFromMetadata(value: unknown): RiskFlowAlert["direction"] {
  if (value === "Bullish" || value === "Bearish" || value === "Neutral")
    return value;
  return null;
}

function weightSourceFromMetadata(
  value: unknown,
): RiskFlowAlert["headlineWeightSource"] | undefined {
  if (
    value === "ivScore" ||
    value === "eventWeight" ||
    value === "macroLevel" ||
    value === "fallback"
  )
    return value;
  return undefined;
}

function subScoresFromMetadata(value: unknown): RiskFlowAlert["subScores"] {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const eventWeight = numberFromMetadata(record.eventWeight);
  const timing = numberFromMetadata(record.timing);
  const deviation = numberFromMetadata(record.deviation);
  const momentum = numberFromMetadata(record.momentum);
  const vixContext = numberFromMetadata(record.vixContext);
  const vixMultiplier = numberFromMetadata(record.vixMultiplier);
  if (
    eventWeight == null ||
    timing == null ||
    deviation == null ||
    momentum == null ||
    vixContext == null ||
    vixMultiplier == null
  )
    return null;
  return {
    ...record,
    eventWeight,
    timing,
    deviation,
    momentum,
    vixContext,
    vixMultiplier,
  } as RiskFlowAlert["subScores"];
}
